import type { ActionContext, ActionTree, Store } from "vuex";
import type { State } from "@/store/state";
import type { AdminState } from "./state";
import type { IUserProfileCreate, IUserProfileUpdate } from "@/interfaces";
import { backend } from "@/api/backend";

type AdminContext = ActionContext<AdminState, State>;

export const actions: ActionTree<AdminState, State> = {
  async actionGetUsers(context: AdminContext): Promise<void> {
    try {
      const response = await backend.getUsers(context.rootState.main.token);
      if (response) {
        context.commit("setUsers", response.data);
      }
    } catch (error) {
      await context.dispatch("main/actionCheckApiError", error, { root: true });
    }
  },
  async actionUpdateUser(
    context: AdminContext,
    payload: { id: number; user: IUserProfileUpdate },
  ): Promise<void> {
    try {
      const loadingNotification = { content: "saving", showProgress: true };
      context.commit("main/addNotification", loadingNotification, {
        root: true,
      });
      const response = (
        await Promise.all([
          backend.updateUser(
            context.rootState.main.token,
            payload.id,
            payload.user,
          ),
          await new Promise<void>((resolve) =>
            setTimeout(() => resolve(), 500),
          ),
        ])
      )[0];
      context.commit("setUser", response.data);
      context.commit("main/removeNotification", loadingNotification, {
        root: true,
      });
      context.commit(
        "main/addNotification",
        {
          content: "User successfully updated",
          color: "success",
        },
        { root: true },
      );
    } catch (error) {
      await context.dispatch("main/actionCheckApiError", error, { root: true });
    }
  },
  async actionDeleteUser(
    context: AdminContext,
    payload: { id: number },
  ): Promise<void> {
    try {
      const loadingNotification = {
        content: `Deleting user with id ${payload.id}`,
        showProgress: true,
      };
      context.commit("main/addNotification", loadingNotification, {
        root: true,
      });
      await Promise.all([
        backend.deleteUser(context.rootState.main.token, payload.id),
        await new Promise<void>((resolve) => setTimeout(() => resolve(), 500)),
      ]);
      context.commit("main/removeNotification", loadingNotification, {
        root: true,
      });
      context.commit(
        "main/addNotification",
        {
          content: "User successfully removed",
          color: "success",
        },
        { root: true },
      );
      await context.dispatch("actionGetUsers");
    } catch (error) {
      await context.dispatch("main/actionCheckApiError", error, { root: true });
    }
  },
  async actionCreateUser(
    context: AdminContext,
    payload: IUserProfileCreate,
  ): Promise<void> {
    try {
      const loadingNotification = { content: "saving", showProgress: true };
      context.commit("main/addNotification", loadingNotification, {
        root: true,
      });
      const response = (
        await Promise.all([
          backend.createUser(context.rootState.main.token, payload),
          await new Promise<void>((resolve) =>
            setTimeout(() => resolve(), 500),
          ),
        ])
      )[0];
      context.commit("setUser", response.data);
      context.commit("main/removeNotification", loadingNotification, {
        root: true,
      });
      context.commit(
        "main/addNotification",
        {
          content: "User successfully created",
          color: "success",
        },
        { root: true },
      );
    } catch (error) {
      await context.dispatch("main/actionCheckApiError", error, { root: true });
    }
  },
};

const NS = "admin/";

export const dispatchCreateUser = (
  store: Store<State>,
  payload: IUserProfileCreate,
) => store.dispatch(`${NS}actionCreateUser`, payload);
export const dispatchGetUsers = (store: Store<State>) =>
  store.dispatch(`${NS}actionGetUsers`);
export const dispatchUpdateUser = (
  store: Store<State>,
  payload: { id: number; user: IUserProfileUpdate },
) => store.dispatch(`${NS}actionUpdateUser`, payload);
export const dispatchDeleteUser = (
  store: Store<State>,
  payload: { id: number },
) => store.dispatch(`${NS}actionDeleteUser`, payload);
